import { useEffect, useId } from "react"
import { create } from "zustand"
import { Columns } from "@phosphor-icons/react"

import { useViewHotkeys } from "~/components/planning/useViewHotkeys"
import { useIsCoveredByFullPageDialog } from "~/components/reusable/dialogs/fullPageDialogLayer"
import type { PageCommand } from "~/components/sidebar/search/types"
import { useRegisterSearchCommands } from "~/components/sidebar/search/useRegisterSearchCommands"
import { VIEW_SHORTCUTS } from "~/constants/hotkeys"
import { useColumnChooserV2 } from "./useColumnChooserV2"

type OpenColumnChooserStore = {
  /** Table whose chooser was asked to open; the chooser clears it once it opens */
  requestedTableId: string | null
  request: (tableId: string) => void
  clear: () => void
  /** Tables offering the command, in mount order; only the first registers it */
  claims: readonly { key: string; tableId: string }[]
  claim: (key: string, tableId: string) => void
  release: (key: string) => void
}

export const useOpenColumnChooserStore = create<OpenColumnChooserStore>((set) => ({
  requestedTableId: null,
  request: (tableId) => set({ requestedTableId: tableId }),
  clear: () => set({ requestedTableId: null }),
  claims: [],
  claim: (key, tableId) => set((state) => ({ claims: [...state.claims, { key, tableId }] })),
  release: (key) => set((state) => ({ claims: state.claims.filter((c) => c.key !== key) })),
}))

/** Whether V C opens this table's chooser, so only that chooser advertises the shortcut */
export const isShortcutTarget = (state: OpenColumnChooserStore, tableId: string) =>
  state.claims[0]?.tableId === tableId

/**
 * "Open column chooser" (V C) for the page's main table: the first mounted table that calls this,
 * so a page with two tables registers one command. The chooser opens itself on the request, and
 * DisplayOptionsPopover opens first when the chooser sits inside it.
 */
export function useOpenColumnChooserCommand(tableId: string | undefined) {
  const enabled = useColumnChooserV2()
  const key = useId()
  const covered = useIsCoveredByFullPageDialog()
  const claim = useOpenColumnChooserStore((s) => s.claim)
  const release = useOpenColumnChooserStore((s) => s.release)
  const request = useOpenColumnChooserStore((s) => s.request)
  const isMain = useOpenColumnChooserStore((s) => s.claims[0]?.key === key)

  useEffect(() => {
    if (!enabled || !tableId) return
    claim(key, tableId)
    return () => release(key)
  }, [enabled, tableId, key, claim, release])

  const commands: PageCommand[] =
    isMain && tableId
      ? [
          {
            id: "table.openColumnChooser",
            shortcut: VIEW_SHORTCUTS.openColumnChooser.sequence,
            name: "Open column chooser",
            keywords: ["columns", "show", "hide"],
            icon: <Columns />,
            run: () => request(tableId),
          },
        ]
      : []

  useViewHotkeys(() => commands, isMain && !covered, [VIEW_SHORTCUTS.openColumnChooser])
  useRegisterSearchCommands(commands, [isMain, tableId])
}
